import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
    Add as AddIcon,
    Edit as EditIcon,
    ExpandMore as ExpandMoreIcon,
    FileCopy as CopyIcon,
} from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import {
    PromptRenderRequest,
    PromptTemplate,
    PromptTemplateCreate,
} from '../../types';
import {
    createPromptTemplate,
    getPromptTemplates,
    renderPrompt,
    updatePromptTemplate,
} from '../../services/api';
import PromptTemplateDialog from './PromptTemplateDialog';

const PromptTemplateManagementPanel: React.FC = () => {
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] =
        useState<PromptTemplate | null>(null);
    const [categoryFilter, setCategoryFilter] = useState<string>('');
    const [testingTemplate, setTestingTemplate] =
        useState<PromptTemplate | null>(null);
    const [testVariables, setTestVariables] = useState<Record<string, string>>({});
    const [renderResult, setRenderResult] = useState<string>('');

    const queryClient = useQueryClient();

    const {
        data: templates = [],
        isLoading,
        error,
    } = useQuery({
        queryKey: ['promptTemplates', categoryFilter],
        queryFn: () => getPromptTemplates(categoryFilter || undefined),
    });

    const createMutation = useMutation({
        mutationFn: createPromptTemplate,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['promptTemplates'] });
            setDialogOpen(false);
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({
                         id,
                         data,
                     }: {
            id: number;
            data: Partial<PromptTemplateCreate>;
        }) => updatePromptTemplate(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['promptTemplates'] });
            setDialogOpen(false);
        },
    });

    const renderMutation = useMutation({
        mutationFn: renderPrompt,
        onSuccess: (data) => {
            setRenderResult(data.rendered_prompt);
        },
    });

    const handleCreate = () => {
        setEditingTemplate(null);
        setDialogOpen(true);
    };

    const handleEdit = (template: PromptTemplate) => {
        setEditingTemplate(template);
        setDialogOpen(true);
    };

    const handleCopy = (template: PromptTemplate) => {
        const newName = `${template.name} (Copy)`;
        const newCategory = template.category;

        const cleanTemplateData: PromptTemplateCreate = {
            name: newName,
            category: newCategory,
            task_type: template.task_type,
            template: template.template,
            description: template.description || '',
            variables: template.variables || [],
            default_values: template.default_values || {},
            compatible_services: template.compatible_services || [],
            version: template.version,
            is_active: true,
            priority: template.priority,
        };

        setEditingTemplate(cleanTemplateData as PromptTemplate);
        setDialogOpen(true);
    };


    const handleSave = (data: PromptTemplateCreate) => {
        if (editingTemplate?.id) {
            updateMutation.mutate({ id: editingTemplate.id, data });
        } else {
            createMutation.mutate(data);
        }
    };

    const handleActiveToggle = (id: number, currentStatus: boolean) => {
        const template = templates.find((t) => t.id === id);
        if (template) {
            updateMutation.mutate({
                id,
                data: {
                    ...template,
                    is_active: !currentStatus,
                },
            });
        }
    };

    const executeTest = () => {
        if (!testingTemplate) return;

        const request: PromptRenderRequest = {
            template_id: testingTemplate.id,
            variables: testVariables,
        };

        renderMutation.mutate(request);
    };

    const categories = Array.from(new Set(templates.map((t) => t.category))).sort();

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 70 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            sortable: false,
            renderCell: (params) => (
                <Box>
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEdit(params.row)}
                        aria-label="edit"
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => handleCopy(params.row)}
                        aria-label="copy"
                    >
                        <CopyIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
        {
            field: 'name',
            headerName: 'Name',
            width: 250,
        },
        { field: 'category', headerName: 'Category', width: 130 },
        { field: 'task_type', headerName: 'Task Type', width: 150 },
        {
            field: 'is_active',
            headerName: 'Status',
            width: 120,
            renderCell: (params) => (
                <Chip
                    icon={params.value ? <CheckCircleIcon /> : <CancelIcon />}
                    label={params.value ? 'Active' : 'Inactive'}
                    color={params.value ? 'success' : 'error'}
                    onClick={() => handleActiveToggle(params.row.id, params.value)}
                    size="small"
                />
            ),
        },
        { field: 'priority', headerName: 'Priority', width: 100, type: 'number' },
        { field: 'usage_count', headerName: 'Usage', width: 100, type: 'number' },
        { field: 'version', headerName: 'Version', width: 100 },
        {
            field: 'variables',
            headerName: 'Variables',
            width: 150,
            renderCell: (params) => (
                <Typography
                    variant="caption"
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                    {params.value?.length ? `${params.value.length} vars` : 'None'}
                </Typography>
            ),
        },
        {
            field: 'created_at',
            headerName: 'Created',
            type: 'dateTime',
            width: 140,
            valueGetter: (params) => new Date(params.value),
        },
    ];

    if (error) {
        return (
            <Alert severity="error">
                Error loading prompt templates: {(error as Error).message}
            </Alert>
        );
    }

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                }}
            >
                <Typography variant="h5">Prompt Template Management</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                >
                    Create Template
                </Button>
            </Box>

            <Box sx={{ mb: 3 }}>
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by Category</InputLabel>
                    <Select
                        value={categoryFilter}
                        label="Filter by Category"
                        onChange={(e) => setCategoryFilter(e.target.value)}
                    >
                        <MenuItem value="">All Categories</MenuItem>
                        {categories.map((category) => (
                            <MenuItem key={category} value={category}>
                                {category}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Paper
                sx={{
                    p: 2,
                    height: '70vh',
                    mt: 2,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box sx={{ flexGrow: 1 }}>
                    <DataGrid
                        rows={templates}
                        columns={columns}
                        loading={isLoading}
                        sx={{ border: 0 }}
                        initialState={{
                            pagination: { page: 0, pageSize: 10 },
                        }}
                        disableSelectionOnClick
                        getRowId={(row) => row.id}
                    />
                </Box>
            </Paper>

            {testingTemplate && (
                <Accordion sx={{ mt: 3 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>
                            🧪 Testing Template: {testingTemplate.name}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            <Typography variant="h6">Fill in Variables:</Typography>

                            {testingTemplate.variables?.map((variable) => (
                                <TextField
                                    key={variable}
                                    label={variable}
                                    value={testVariables[variable] || ''}
                                    onChange={(e) =>
                                        setTestVariables((prev) => ({
                                            ...prev,
                                            [variable]: e.target.value,
                                        }))
                                    }
                                    fullWidth
                                    placeholder={`Enter value for {${variable}}`}
                                />
                            ))}

                            <Button
                                variant="contained"
                                onClick={executeTest}
                                disabled={renderMutation.status === 'loading'}
                                sx={{ mt: 2 }}
                            >
                                {renderMutation.status === 'loading'
                                    ? 'Rendering...'
                                    : 'Test Template'}
                            </Button>

                            {renderResult && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Rendered Result:
                                    </Typography>
                                    <TextField
                                        multiline
                                        rows={10}
                                        value={renderResult}
                                        fullWidth
                                        InputProps={{ readOnly: true }}
                                        sx={{ bgcolor: 'grey.50' }}
                                    />
                                </Box>
                            )}

                            {renderMutation.isError && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    Rendering error:{' '}
                                    {(renderMutation.error as Error)?.message}
                                </Alert>
                            )}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )}

            <PromptTemplateDialog
                open={isDialogOpen}
                onClose={() => {
                    setDialogOpen(false);
                    setEditingTemplate(null);
                }}
                template={editingTemplate}
                onSave={handleSave}
            />
        </Box>
    );
};

export default PromptTemplateManagementPanel;